import { useEffect, useRef, useState } from "react";
import { createAudioIndicator, createAudioLevel, stopAudioIndicator } from "../../modules/audio";
import { useParticipantsStore } from "../store/participants.store";
import { useMediaDevicesStore } from "../store/media-devices.store";
import { localStreamingService, remoteStreamingService } from "../../modules/multiplayer/peer";
import { preferencesService } from "../../modules/preferences/PreferencesService";
export function useLocalMicLevel() {
    const [level, setLevel] = useState(0);
    const [threshold, setThreshold] = useState(0);
    const audioIndicator = useRef(null);
    const micDeviceId = useMediaDevicesStore((state) => state.mic.deviceId);
    const cleanup = () => {
        setLevel(0);
        if (audioIndicator.current != null) {
            stopAudioIndicator(audioIndicator.current);
            audioIndicator.current = null;
        }
    };
    const setup = () => {
        cleanup();
        navigator.mediaDevices
            .getUserMedia(getAudioConstraints())
            .then((stream) => {
            if (stream) {
                if (stream.getAudioTracks().length === 0) {
                    console.error("[ui] User media stream has no audio tracks", stream);
                    return;
                }
                const indicator = createAudioLevel(stream, (level, threshold) => {
                    setLevel(level);
                    setThreshold(threshold);
                });
                if (indicator) {
                    audioIndicator.current = indicator;
                }
                else {
                    console.error("[ui] Error while creating audio indicator");
                }
            }
            // else {
            //   console.warn("[ui] No mic stream available")
            // }
        })
            .catch((err) => {
            console.warn("[ui] Error while getting mic stream", err);
        });
    };
    useEffect(() => {
        setup();
        return () => {
            cleanup();
        };
    }, []);
    useEffect(() => {
        setup();
    }, [micDeviceId]);
    return [level, threshold];
}
export function useLocalMicIndicator() {
    const [isSpeaking, setIsSpeaking] = useState(false);
    const audioIndicator = useRef(null);
    const cleanUp = () => {
        setIsSpeaking(false);
        if (audioIndicator.current) {
            stopAudioIndicator(audioIndicator.current);
            audioIndicator.current = null;
        }
    };
    const updateAudioIndicator = () => {
        const stream = localStreamingService.getMicrophoneStream();
        if (stream) {
            if (stream.getAudioTracks().length === 0) {
                console.error("[ui] Peer stream has no audio tracks", stream);
                return;
            }
            const indicator = createAudioIndicator(stream, () => {
                setIsSpeaking(true);
            }, () => {
                setIsSpeaking(false);
            });
            if (indicator) {
                audioIndicator.current = indicator;
            }
            else {
                console.error("[ui] Error while creating audio indicator");
            }
        }
        else {
            cleanUp();
            // console.warn("[ui] No mic stream available")
        }
    };
    useEffect(() => {
        localStreamingService.onUpdateDelegate.add(updateAudioIndicator);
        updateAudioIndicator();
        return () => {
            localStreamingService.onUpdateDelegate.remove(updateAudioIndicator);
            cleanUp();
        };
    }, []);
    return isSpeaking;
}
export function useRemoteMicIndicator() {
    const otherUsers = useParticipantsStore((state) => state.otherUsers());
    const [speakingUsers, setSpeakingUsers] = useState([]);
    const audioIndicators = useRef(new Map());
    const [remoteMicStreams, setRemoteMicStreams] = useState([]);
    useEffect(() => {
        const updateStreams = () => {
            setRemoteMicStreams(remoteStreamingService.getMicrophoneStreams());
        };
        remoteStreamingService.onUpdateDelegate.add(updateStreams);
        updateStreams();
        return () => {
            remoteStreamingService.onUpdateDelegate.remove(updateStreams);
        };
    }, []);
    useEffect(() => {
        let currentUsers = Array.from(audioIndicators.current.keys());
        // Remove old users
        const oldUsers = currentUsers.filter((id) => !otherUsers.find((user) => user.clientId === id));
        oldUsers.forEach((id) => {
            const audioIndicator = audioIndicators.current.get(id);
            if (audioIndicator != null) {
                stopAudioIndicator(audioIndicator);
                audioIndicators.current.delete(id);
            }
        });
        currentUsers = Array.from(audioIndicators.current.keys());
        // Add new users
        const newUsers = otherUsers.filter((user) => !currentUsers.find((id) => id === user.clientId));
        newUsers.forEach((user) => {
            var _a;
            const stream = (_a = remoteMicStreams.find((remote) => remote.clientId === user.clientId)) === null || _a === void 0 ? void 0 : _a.stream;
            if (stream != null) {
                const audioIndicator = createAudioIndicator(stream, () => {
                    setSpeakingUsers((prev) => [...prev, user]);
                }, () => {
                    setSpeakingUsers((prev) => prev.filter((u) => u.clientId !== user.clientId));
                });
                if (audioIndicator) {
                    audioIndicators.current.set(user.clientId, audioIndicator);
                }
                else {
                    console.error("[ui] Error while creating the audio indicator");
                }
            }
        });
    }, [otherUsers, remoteMicStreams]);
    useEffect(() => {
        return () => {
            audioIndicators.current.forEach((audioIndicator) => {
                stopAudioIndicator(audioIndicator);
            });
            audioIndicators.current.clear();
        };
    }, []);
    return speakingUsers;
}
function getAudioConstraints() {
    const audioConstraints = {};
    const deviceId = useMediaDevicesStore.getState().mic.deviceId;
    if (deviceId !== undefined) {
        audioConstraints.deviceId = { exact: deviceId };
    }
    else {
        audioConstraints.deviceId = preferencesService.getPreferredValue("microphoneDeviceId", "default");
    }
    function getConstrainBoolean(value) {
        switch (value) {
            case "true":
                return true;
            case "false":
                return false;
            case "ideal":
                return { ideal: true };
            case "exact":
                return { exact: true };
            default:
        }
    }
    const voiceEchoCancellation = getConstrainBoolean(preferencesService.getPreferredValue("voiceEchoCancellation", "exact"));
    if (voiceEchoCancellation != undefined) {
        audioConstraints.echoCancellation = voiceEchoCancellation;
    }
    const voiceNoiseSuppression = getConstrainBoolean(preferencesService.getPreferredValue("voiceNoiseSuppression", "exact"));
    if (voiceNoiseSuppression != undefined) {
        audioConstraints.noiseSuppression = voiceNoiseSuppression;
    }
    const voiceAutoGainControl = getConstrainBoolean(preferencesService.getPreferredValue("voiceAutoGainControl", "exact"));
    if (voiceAutoGainControl != undefined) {
        audioConstraints.autoGainControl = voiceAutoGainControl;
    }
    const constraints = {
        video: false,
        audio: filterSupportedAudioConstraints(audioConstraints)
    };
    return constraints;
}
function filterSupportedAudioConstraints(audioDeviceConstraints) {
    try {
        const matchConstraints = {};
        const supportedConstraints = navigator.mediaDevices.getSupportedConstraints() || {};
        for (const [constraintName, constraintValue] of Object.entries(audioDeviceConstraints)) {
            if (!supportedConstraints[constraintName])
                continue;
            matchConstraints[constraintName] = constraintValue;
        }
        return matchConstraints;
    }
    catch (error) {
        console.error("[UI] Unsupported audio constraints", error);
        return {};
    }
}
